/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ITextField } from '@fluentui/react/lib/TextField';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylist, important } from 'src/core/themes/luisStylist';
import { noop } from 'src/core/utils/base';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';
import { extractPatternEntities } from 'src/luis/modules/patterns/extractPatternEntities';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { BracesHandler } from 'src/luis/verticals/patternInput/components/BracesHandler';
import { PatternInputProps } from 'src/luis/verticals/patternInput/patternInputProps';
import { PatternDropdownType } from 'src/luis/verticals/patternInput/types';
import { parse } from 'src/luis/verticals/patternInput/utils/generated-pattern-parser-validator';
import { postProcessText, preProcessText, setCursorPosition } from 'src/luis/verticals/patternInput/utils/patternInputParsers';

const messages = defineMessages({
    placeholder: {
        id: 'patterns.patternInput.placeholder',
        defaultMessage: 'Type in templates of what you expect your users to say and hit Enter.'
    },
    selectIntentFirst: {
        id: 'patterns.patternInput.selectIntentFirst',
        defaultMessage: 'Please Select an intent first'
    },
    syntaxErrorMessage: {
        id: 'patterns.patternInput.syntaxErrorMessage',
        defaultMessage: 'Syntax error at character #{ position }'
    },
    entitiesErrorMessage: {
        id: 'patterns.patternInput.entitiesErrorMessage',
        defaultMessage: "'{ entity }' is not a valid entity"
    },
    intentsPlaceholder: {
        id: 'patterns.patternInput.intentsPlaceholder',
        defaultMessage: 'Select an intent'
    },
    emptyEntityName: {
        id: 'patterns.patternInput.emptyEntityName',
        defaultMessage: "'Empty String'"
    }
});

const { getClassName } = getStylist('PatternInput');

const patternInputClassName = getClassName('PatternInput', theme => ({
    fontSize: theme.fontSize,
    lineHeight: theme.lineHeight,
    letterSpacing: theme.letterSpacing,
    ...important({ fontFamily: 'monospace' })
}));

/**
 * The pattern input field to process the pattern text.
 */
export const PatternInput = (props: PatternInputProps) => {
    const {
        disabled,
        entities,
        initialPattern = '',
        onPatternSubmit,
        onPatternChange,
        onCreateNewEntity,
        onCreateNewRole,
        onCreateNewEntityAndRole,
        onExitEditMode = noop
    } = props;
    const { localize } = useLocalization();
    const [error, setError] = React.useState('');
    const [focusOnMenu, setFocusOnMenu] = React.useState(false);
    const [dropdown, setDropdown] = React.useState<PatternDropdownType>('none');
    const [searchableString, setSearchableString] = React.useState('');
    const [currentText, setCurrentText] = React.useState(initialPattern);
    const textFieldWrapperRef = React.useRef<HTMLDivElement>();
    const textFieldRef = React.useRef<ITextField>(null);
    const placeHolderMessage = disabled ? localize(messages.selectIntentFirst) : localize(messages.placeholder);

    /**
     * Auto reset the pattern error whenever
     * the text changes.
     */
    React.useEffect(() => setError(''), [currentText]);

    /**
     * Reflects the selection of item in the contextual menu
     * either entity or role.
     *
     * @param item The selected item.
     */
    const onItemSelected = (item: string): void => {
        const openBracketIndex = currentText.slice(0, textFieldRef.current.selectionStart).lastIndexOf('{');
        const closeBracketIndex = currentText.slice(textFieldRef.current.selectionStart).indexOf('}');
        const prefix = currentText.slice(0, openBracketIndex + 1);
        const suffix = currentText
            .slice(textFieldRef.current.selectionStart)
            .slice(closeBracketIndex !== -1 ? closeBracketIndex : currentText.length - 1);

        setCurrentText(`${prefix}${item}${suffix}`);
        setCursorPosition(textFieldRef.current, `${prefix}${item}`.length + 1, setDropdown, noop);
    };

    /**
     * Applies pre processing to the current text.
     *
     * @param event The event that was fired by the key.
     */
    const onKeyDown = (event: React.KeyboardEvent) => {
        if (dropdown !== 'none' && (event.key === LuisKeyCodes.ArrowUp || event.key === LuisKeyCodes.ArrowDown)) {
            event.preventDefault();
            setFocusOnMenu(true);
        } else {
            setFocusOnMenu(false);
        }

        setCurrentText(preProcessText(currentText, textFieldRef.current, event, setSearchableString, setDropdown));
    };

    /**
     * Fired when the pattern text field's key is up. Executes
     * the post text change pattern text processors.
     */
    const onKeyUp = (event: React.KeyboardEvent) => {
        if (event.key === LuisKeyCodes.Escape) {
            onExitEditMode();

            return;
        }

        if (event.key === LuisKeyCodes.Enter) {
            onEnterKeyUp();

            return;
        }

        setCurrentText(postProcessText(currentText, textFieldRef.current, setDropdown, setSearchableString));

        const validationError = validate(currentText, entities, localize);

        if (onPatternChange) {
            onPatternChange(currentText, validationError !== undefined);
        }
    };

    /**
     * Validates the current pattern text in the field. If
     * it is valid, submits the pattern text to the parent
     * component for and resets the fields.
     */
    const onEnterKeyUp = () => {
        if (focusOnMenu) {
            return;
        }

        setDropdown('none');
        const validationError = validate(currentText, entities, localize);

        if (validationError) {
            setError(validationError);
        } else if (onPatternSubmit) {
            onPatternSubmit(currentText);

            /**
             * @todo not understood behavior .. will revisit later .. reference [Bug 9405]
             */
            setTimeout(() => setCurrentText(''), 0);
        }
    };

    /**
     * This function captures the key down events that occur
     * when the user is focused on the dropdown. The behavior
     * should be:
     *
     * - If the user presses the left or right arrow keys,
     *   the dropdown is closed, and the focus returns to
     *   the text field.
     */
    const onDropdownKeyDown = (event: React.KeyboardEvent) => {
        if (dropdown !== 'none' && (event.key === LuisKeyCodes.ArrowLeft || event.key === LuisKeyCodes.ArrowRight)) {
            setFocusOnMenu(false);
            textFieldRef.current.focus();
        }
    };

    return (
        <div ref={textFieldWrapperRef}>
            <LuisTextField
                autoFocus
                disabled={disabled}
                styles={{ field: patternInputClassName }}
                ariaLabel={placeHolderMessage}
                placeholder={placeHolderMessage}
                componentRef={textFieldRef}
                errorMessage={error}
                value={currentText}
                onKeyDown={onKeyDown}
                onChange={(_, text) => setCurrentText(text)}
                onKeyUp={onKeyUp}
            />

            <div onKeyDownCapture={onDropdownKeyDown}>
                <BracesHandler
                    parentRef={textFieldWrapperRef.current}
                    entities={entities}
                    focus={focusOnMenu}
                    position={textFieldRef.current ? textFieldRef.current.selectionStart : 0}
                    dropdownType={dropdown}
                    queryText={searchableString}
                    onCreateNewEntity={onCreateNewEntity}
                    onCreateNewRole={onCreateNewRole}
                    onItemClicked={onItemSelected}
                    onCreateNewEntityAndRole={onCreateNewEntityAndRole}
                />
            </div>
        </div>
    );
};

/**
 * Validates the submitted pattern, checks that:
 * 1. The syntax of the pattern is correct.
 * 2. All mentioned entities exist.
 *
 * @param pattern The pattern to validate.
 * @param entities The entities in the application.
 * @param localize The callback that does the localization stuff.
 * @returns The error message if there was an error or undefined
 * if there wasn't.
 */
export const validate = (
    patternText: string,
    entities: Entity[],
    localize: ReturnType<typeof useLocalization>['localize']
): string | undefined => {
    try {
        parse(patternText);
    } catch (error) {
        return localize(messages.syntaxErrorMessage, { position: error.hash.loc.last_column });
    }

    const entitiesAndRoles = entities.reduce<string[]>((acc, e) => [...acc, e.name, ...e.roles.map(r => `${e.name}:${r.name}`)], []);
    const textWithIgnoredEscapedBrackets = patternText.replace(/\\\\|\\[{}]/g, '');
    const extractedEntities = extractPatternEntities(textWithIgnoredEscapedBrackets).filter(e => entitiesAndRoles.every(eR => eR !== e));

    if (extractedEntities.length > 0) {
        const entity = extractedEntities[extractedEntities.length - 1] || localize(messages.emptyEntityName);

        return localize(messages.entitiesErrorMessage, { entity });
    }

    return undefined;
};
